import React, { useState } from "react";
import { useAuth } from "../Context/AuthContext";
import "../Styling/ProjectProgressDashboard.css";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Base_URL } from "../url";
import { useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ProjectProgressList = () => {
  const { user } = useAuth();
  const location = useLocation();

  const [filters, setFilters] = useState({
    type: "All",
    client: "",
    project: "",
    resource: "",
    fromDate: "",
    toDate: "",
    dueDate: "",
    dueStatus: "",
    status: "All",

    // ✅ NEW FILTERS
    changedFromDate: "",
    changedToDate: "",
    submittedBy: "",
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [dropdownData, setDropdownData] = useState({
    clients: [],
    projects: [],
    types: [],
    resources: [],
    submittedBy: [],
    dueStatus: "",
    status: [],
  });

  // ── SORT STATE (only change #1) ──────────────────────────
  // sortField: null | "createdOn" | "chargedOn"
  // sortDir:   "desc" (newest first) | "asc" (oldest first)
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const handleSortToggle = (field) => {
    if (sortField === field) {
      // Same column clicked → flip direction
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      // New column clicked → sort descending (newest first)
      setSortField(field);
      setSortDir("desc");
    }
  };

  // Derived sorted data — keeps tableData state untouched
  const sortedData = React.useMemo(() => {
    if (!sortField) return tableData;
    return [...tableData].sort((a, b) => {
      const dateA = a[sortField] ? new Date(a[sortField]) : new Date(0);
      const dateB = b[sortField] ? new Date(b[sortField]) : new Date(0);
      return sortDir === "desc" ? dateB - dateA : dateA - dateB;
    });
  }, [tableData, sortField, sortDir]);

  // Helper: show arrow indicator in header
  const sortArrow = (field) => {
    if (sortField !== field) return " ↕";
    return sortDir === "desc" ? " ▼" : " ▲";
  };
  // ────────────────────────────────────────────────────────

  const handleDownloadExcel = () => {
    if (!tableData || tableData.length === 0) {
      alert("No data to export");
      return;
    }

    const formattedData = tableData.map((row) => ({
      "Org Name": row.orgDisplayName,
      "Client Name": row.clientDisplayName,
      "Project Name": row.projectName,
      "Resource": row.resourceId,
      "Due Date": row.dueDate
        ? new Date(row.dueDate).toLocaleDateString()
        : "",
      "Due Status": row.dueStatus,
      "Created On": row.createdOn
        ? new Date(row.createdOn).toLocaleDateString()
        : "",
      "Charged On": row.chargedOn
        ? new Date(row.chargedOn).toLocaleDateString()
        : "",
      "Status": row.status,
      "Priority": row.priority,
      "Ticket/Task ID": row.ticketId,
      "Title": row.title,
      "Type": row.type,
      "Submitted By": row.submittedBy,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // ✅ AUTO WIDTH CALCULATION
    const columnWidths = Object.keys(formattedData[0]).map((key) => {
      const maxLength = Math.max(
        key.length, // header length
        ...formattedData.map((row) =>
          row[key] ? row[key].toString().length : 0
        )
      );

      return { wch: maxLength + 2 }; // padding
    });

    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Project Progress");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, "Project_Progress_List.xlsx");
  };

  const handleChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleApply = async (isManual = true) => {
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => {
          if (value === "" || value === null) return false;
          if (value === "All") return false;
          return true;
        }),
      );


      if (isManual) {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, val]) => {
          if (val && val !== "All") {
            params.set(key, val);
          }
        });

        window.history.replaceState(null, "", `?${params.toString()}`);
      }


      const res = await axios.get(`${Base_URL}/ProjectProgress/list`, {
        params: {
          ...cleanedFilters,
          orgId: user?.organisationId,
        },
      });

      const data = res?.data?.data || res?.data?.Data || res?.data || [];

      // ✅ 🔥 NORMALIZE DATA HERE
      const mappedData = (Array.isArray(data) ? data : []).map((item) => ({
        ...item,
        status: item.status ?? item.Status ?? item.STATUS ?? "",
        orgDisplayName:
          item.orgShortName && item.orgShortName.trim()
            ? item.orgShortName
            : item.orgName,

        clientDisplayName: item.clientShortName || "",
      }));
      // ✅ Extract unique clientShortNames
      const clientShortNames = [
        ...new Set(
          mappedData
            .map(item => item.clientDisplayName)
            .filter(name => name && name.trim() !== "")
        )
      ];

      setDropdownData(prev => ({
        ...prev,
        clients: clientShortNames
      }));

      const filteredData = mappedData.filter(item => {
        const date = new Date(item.chargedOn);

        if (filters.fromDate && date < new Date(filters.fromDate)) return false;
        if (filters.toDate && date > new Date(filters.toDate)) return false;

        if (filters.changedFromDate && date < new Date(filters.changedFromDate)) return false;
        if (filters.changedToDate && date > new Date(filters.changedToDate)) return false;

        if (filters.client && item.clientDisplayName !== filters.client) return false;
        if (filters.dueStatus && item.dueStatus !== filters.dueStatus) return false;
        if (
          filters.status &&
          filters.status !== "All" &&
          (item.status || "").trim().toLowerCase() !== filters.status.trim().toLowerCase()
        ) return false;
        //  if (
        //     filters.type &&
        //     filters.type !== "All" &&
        //     (item.type || "").trim().toLowerCase() !== filters.type.trim().toLowerCase()
        //   ) return false;
        return true;
      });

      setTableData(filteredData);


    } catch (err) {
      console.log("List API error:", err);
    }
  };

  const handleClear = () => {
    const resetFilters = {
      type: "All",
      client: "",
      project: "",
      resource: "",
      fromDate: "",
      toDate: "",
      dueDate: "",
      dueStatus: "",
      status: "All",
      changedFromDate: "",
      changedToDate: "",
      submittedBy: "",
    };

    setFilters(resetFilters);
    handleApply(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const newFilters = {
      type: "All",
      client: "",
      project: "",
      resource: "",
      fromDate: "",
      toDate: "",
      dueDate: "",
      dueStatus: "",
      status: "",
      changedFromDate: "",
      changedToDate: "",
      submittedBy: "",
    };

    for (let [key, value] of params.entries()) {
      newFilters[key] = value;
    }


    setFilters(newFilters);
    setIsInitialized(true);
  }, [location.search]);


  useEffect(() => {
    if (user?.organisationId && isInitialized) {
      handleApply(false); // auto call
    }
  }, [filters, user, isInitialized]);

  //   useEffect(() => {
  //     if (user?.organisationId) {
  //       handleApply();
  //     }
  //   }, [filters]);

  useEffect(() => {
    if (user.organisationId) {
      setFilters((prev) => ({
        ...prev,
        organisationId: user.organisationId,
      }));
    }
  }, [user]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await axios.get(
          `${Base_URL}/ProjectProgress/dropdowns`,
          {
            params: { orgId: user?.organisationId },
          },
        );



        const data = res?.data?.data || res?.data?.Data || res?.data || {};

        setDropdownData({
          clients: data.client ?? [],
          projects: data.projects ?? [],
          types: data.types ?? [],
          resources: data.resources ?? [],
          submittedBy: data.submittedBy ?? [],
          dueStatuses: data.dueStatuses ?? [],
          statuses: data.statuses ?? [],
        });
      } catch (err) {
        console.error("Dropdown API error:", err);
      }
    };

    if (user?.organisationId) {
      fetchDropdowns();
    }
  }, [user]);

  return (
    <div className="ppl-container">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px"
        }}
      >
        <h2 className="ppl-title" style={{ margin: 0 }}>
          Project Progress List
        </h2>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => setShowFilters(prev => !prev)}
            style={{
              backgroundColor: "#687c9a",
              color: "#fff",
              border: "none",
              padding: "8px 12px",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.25s ease",
              boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)",
              fontSize: "13px",
              fontWeight: "500",
              minWidth: "140px",
            }}
          >
            {showFilters ? "Hide Filters ▲" : "Show Filters ▼"}
          </button>

          <button onClick={handleDownloadExcel} className="download-btn">
            ⬇ Download Excel
          </button>
        </div>
      </div>

      {/* ================= FILTERS ================= */}

      {showFilters && (
        <div className="ppd-filter-box">
          {/* Org ID */}

          <div className="ppd-field">
            <label>Organisation ID</label>
            <input value={user.organisationId || ""} disabled />
          </div>

          {/* Type */}
          <div className="ppd-field">
            <label>Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleChange("type", e.target.value)}
              key={dropdownData.types.join(",")}
            >
              <option value="All">All</option>

              {(dropdownData.types || []).map((t, i) => (
                <option key={i} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Client */}
          <div className="ppd-field">
            <label>Client</label>
            <select
              value={filters.client}
              onChange={(e) => handleChange("client", e.target.value)}
            >
              <option value="">Select Client</option>

              {(dropdownData.clients || []).map((c, i) => (
                <option key={i} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Project */}
          <div className="ppd-field">
            <label>Project</label>
            <select
              value={filters.project}
              onChange={(e) => handleChange("project", e.target.value)}
            >
              <option value="">Select Project</option>

              {(dropdownData.projects || []).map((p, i) => (
                <option key={i} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          {/* Resource */}
          <div className="ppd-field">
            <label>Assigned To</label>
            <select
              value={filters.resource}
              onChange={(e) => handleChange("resource", e.target.value)}
            >
              <option value="">Select Resource</option>

              {(dropdownData.resources || []).map((r, i) => (
                <option key={i} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div className="ppd-field">
            <label>Due Date</label>
            <input
              type="date"
              value={filters.dueDate}
              onChange={(e) => handleChange("dueDate", e.target.value)}
            />
          </div>

          <div className="ppd-field">
            <label>Submitted By</label>
            <select
              value={filters.submittedBy}
              onChange={(e) => handleChange("submittedBy", e.target.value)}
            >
              <option value="">Select Submitted By</option>

              {(dropdownData.submittedBy || []).map((s, i) => (
                <option key={i} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <br />

          <div className="ppd-field">
            <label>Due Status</label>
            <select
              value={filters.dueStatus}
              onChange={(e) => handleChange("dueStatus", e.target.value)}
            >
              <option value="">All</option>

              {(dropdownData.dueStatuses || []).map((ds, i) => (
                <option key={i} value={ds}>
                  {ds}
                </option>
              ))}
            </select>
          </div>

          <div className="ppd-field">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              <option value="All">All</option>

              {(dropdownData.statuses || []).map((s, i) => (
                <option key={i} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="ppd-date-row">
            <div className="ppd-field">
              <label>Created From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleChange("fromDate", e.target.value)}
              />
            </div>

            <div className="ppd-field">
              <label>Created To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => handleChange("toDate", e.target.value)}
              />
            </div>
          </div>

          {/* ✅ Changed Date Row */}
          <div className="ppd-date-row">
            <div className="ppd-field">
              <label>Changed From Date</label>
              <input
                type="date"
                value={filters.changedFromDate}
                onChange={(e) => handleChange("changedFromDate", e.target.value)}
              />
            </div>

            <div className="ppd-field">
              <label>Changed To Date</label>
              <input
                type="date"
                value={filters.changedToDate}
                onChange={(e) => handleChange("changedToDate", e.target.value)}
              />
            </div>
          </div>

          <div className="ppd-buttons">
            <button className="apply-btn" onClick={() => handleApply(true)}>
              Apply
            </button>
            <button className="clear-btn" onClick={handleClear}>
              Clear
            </button>
          </div>
        </div>
      )}


      {/* ================= TABLE ================= */}
      <div className="ppl-table-container">
        <table className="ppl-table">
          <thead>
            <tr>
              {/* <th>Org ID</th> */}
              {/* <th>Org Name</th> */}
              {/* <th>Client ID</th> */}
              <th>Client Name</th>
              {/* <th>Project ID</th> */}
              <th style={{ width: "180px" }}>Project Name</th>
              <th>Assigned To</th>
              <th>Due Date</th>
              <th>Due Status</th>

              {/* ── SORT CHANGE #2: make these two headers clickable ── */}
              <th
                onClick={() => handleSortToggle("createdOn")}
                style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                title="Click to sort"
              >
                Created On{sortArrow("createdOn")}
              </th>
              <th
                onClick={() => handleSortToggle("chargedOn")}
                style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                title="Click to sort"
              >
                Changed On{sortArrow("chargedOn")}
              </th>
              {/* ───────────────────────────────────────────────────── */}

              <th>Status</th>
              <th>Priority</th>
              <th>Ticket/Task ID</th>
              <th style={{ width: "220px" }}>Title</th>
              <th>Type</th>
              <th>Submitted By</th>
              {/* <th>Task ID</th> */}
            </tr>
          </thead>

          <tbody>
            {/* ── SORT CHANGE #3: render sortedData instead of tableData ── */}
            {sortedData.length > 0 ? (
              sortedData.map((row, index) => (
                <tr key={index}>
                  {/* <td>{row.orgId}</td> */}
                  {/* <td>{row.orgDisplayName}</td> */}
                  <td>{row.clientDisplayName|| "-"}</td>
                  {/* <td>{row.projectId}</td> */}
                  <td style={{
                    maxWidth: "180px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                    {row.projectId ? (
                      <a
                        href={`/project/${row.projectId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Click to view project details"
                        style={{
                          color: "#03275c",
                          textDecoration: "none",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                        onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                      >
                        {row.projectName || row.projectId|| "-"}
                      </a>
                    ) : (
                      row.projectName || "-"
                    )}
                  </td>
                  <td>{row.resourceId|| "-"}</td>
                  <td>
                    {row.dueDate
                      ? new Date(row.dueDate).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>{row.dueStatus|| "-"}</td>

                  <td>
                    {row.createdOn
                      ? new Date(row.createdOn).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    {row.chargedOn
                      ? new Date(row.chargedOn).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>{row.status|| "-"}</td>
                  <td>{row.priority|| "-"}</td>
                  <td>
                    {row.ticketId ? (
                      row.type?.toLowerCase() === "task" ? (
                        <a
                          href={`/project/${row.projectId}/task/${row.ticketId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Click to task details"
                          style={{
                            color: "#03275c",
                            textDecoration: "none",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                          onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                        >
                          {row.ticketId || "-"}
                        </a>
                      ) : (
                        <Link
                          to={`/ticketinfo/${row.ticketId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Click to view ticket details"
                          style={{
                            color: "#03275c",
                            textDecoration: "none",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                          onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                        >
                          {row.ticketId|| "-"}
                        </Link>
                      )
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td style={{
                    maxWidth: "220px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }} title={row.title}>
                    {row.title}
                  </td>
                  {/* <td>{row.taskId}</td> */}
                  <td>{row.type|| "-"}</td>
                  <td>{row.submittedBy|| "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="16" className="no-data">
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectProgressList;